import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, HelpCircle } from "lucide-react";
import { isStandalone, isMobile } from "@/lib/deviceDetect";
import { PwaInstallGuide } from "@/components/PwaInstallGuide";

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("pwa_install_dismissed") || isStandalone()) return;

    const onInstall = () => {
      setVisible(false);
      localStorage.setItem("pwa_install_dismissed", "1");
    };

    const handler = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferredPrompt(e);
      setMode("native");
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstall);

    const timer = setTimeout(() => {
      if (!deferredRef.current && isMobile()) {
        setMode("manual");
        setVisible(true);
      }
    }, 2500);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstall);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
      localStorage.setItem("pwa_install_dismissed", "1");
    }
    setDeferredPrompt(null);
    deferredRef.current = null;
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa_install_dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-[49] bg-blue-600 text-white shadow-lg safe-area-pb">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Download className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm flex-1">
            {mode === "native" ? (
              <>Installez <strong>Coach</strong> sur votre appareil pour un accès rapide.</>
            ) : (
              <>Ajoutez <strong>Coach</strong> à votre écran d'accueil pour y accéder comme une app.</>
            )}
          </p>
          {mode === "native" ? (
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full font-semibold text-xs"
            >
              Installer
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setGuideOpen(true)}
              className="bg-white text-blue-600 hover:bg-blue-50 rounded-full font-semibold text-xs"
            >
              <HelpCircle className="w-3.5 h-3.5 mr-1" />
              Comment faire
            </Button>
          )}
          <button onClick={handleDismiss} className="text-white/70 hover:text-white" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <PwaInstallGuide open={guideOpen} onOpenChange={setGuideOpen} />
    </>
  );
}

